// Landscape A4 PDF export for the dashboards: KPI tiles, the on-screen charts
// (Recharts / map SVGs, redrawn on white and embedded as JPEG) and tables of
// the figures behind them. No dependencies: text uses the standard Helvetica
// fonts with WinAnsi encoding, as the server's internal/pdfreport does.

const PW = 842;
const PH = 595;
const M = 30;
const CONTENT_TOP = PH - M - 52; // below the page header
const CONTENT_BOTTOM = M + 18; // above the footer
const AVAIL = PW - 2 * M;

export interface PdfKpi { label: string; value: string; sub?: string; color: string }
export interface PdfLegend { label: string; color: string }
export interface PdfChart {
  title: string;
  /** Element holding the chart's SVG (the chart itself, not its legend). */
  el?: Element | null;
  /** Or a ready-made SVG drawn just for the PDF (e.g. a map not on screen). */
  svg?: { markup: string; width: number; height: number };
  legend?: PdfLegend[];
  /** Relative width when several charts share a row (default 1). */
  weight?: number;
  /** A background colour drawn inside the SVG (e.g. the map) to turn white. */
  background?: string;
}
export interface PdfColumn { label: string; align?: 'left' | 'right' }
export type PdfBlock =
  | { kind: 'kpis'; items: PdfKpi[] }
  | { kind: 'charts'; height: number; items: PdfChart[] }
  | { kind: 'table'; title: string; columns: PdfColumn[]; rows: string[][]; total?: string[] }
  | { kind: 'note'; text: string }
  | { kind: 'pageBreak' };
export interface PdfSpec {
  title: string;
  /** Bold red line under the title, as in the dashboard headers: data dates and time. */
  status?: string;
  subtitle: string;
  fileName: string;
  blocks: PdfBlock[];
}

// ---- text -------------------------------------------------------------------

// Adobe Helvetica advance widths for ASCII 32..126.
const HELV = [
  278, 278, 355, 556, 556, 889, 667, 191, 333, 333, 389, 584, 278, 333, 278, 278,
  556, 556, 556, 556, 556, 556, 556, 556, 556, 556,
  278, 278, 584, 584, 584, 556, 1015,
  667, 667, 722, 722, 667, 611, 778, 722, 278, 500, 667, 556, 833,
  722, 778, 667, 778, 722, 667, 611, 722, 667, 944, 667, 667, 611,
  278, 278, 278, 469, 556, 333,
  556, 556, 500, 556, 556, 278, 556, 556, 222, 222, 500, 222, 833,
  556, 556, 556, 556, 333, 500, 278, 556, 500, 722, 500, 500, 500,
  334, 260, 334, 584,
];

const charW = (c: string) => {
  const n = c.charCodeAt(0);
  return n >= 32 && n < 127 ? HELV[n - 32] : 556;
};
/** Width in points; bold is about 6% wider. */
const textW = (s: string, size: number, bold = false) => {
  let n = 0;
  for (const c of s) n += charW(c);
  return (n * size * (bold ? 1.06 : 1)) / 1000;
};
const fit = (s: string, w: number, size: number, bold = false) => {
  if (textW(s, size, bold) <= w) return s;
  let out = s;
  while (out.length > 1 && textW(out + '...', size, bold) > w) out = out.slice(0, -1);
  return out.trimEnd() + '...';
};
const wrap = (s: string, w: number, size: number) => {
  const lines: string[] = [];
  let line = '';
  for (const word of s.split(/\s+/)) {
    const next = line ? `${line} ${word}` : word;
    if (line && textW(next, size) > w) { lines.push(line); line = word; } else line = next;
  }
  if (line) lines.push(line);
  return lines;
};

/** A PDF string literal in WinAnsi. */
const pdfStr = (s: string) => {
  let out = '';
  for (const c of s) {
    const n = c.charCodeAt(0);
    if (c === '(' || c === ')' || c === '\\') out += '\\' + c;
    else if (n >= 32 && n < 127) out += c;
    else if (c === '·' || c === '•') out += '\\267';
    else if (c === '–' || c === '—') out += '-';
    else if (c === '‘' || c === '’') out += "'";
    else if (c === '≥') out += '>=';
    else if (c === '≤') out += '<=';
    else if (n >= 160 && n < 256) out += '\\' + n.toString(8).padStart(3, '0');
    else if (/\s/.test(c)) out += ' ';
    else out += '?';
  }
  return `(${out})`;
};

const rgb = (hex: string) => {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const v = [0, 2, 4].map((i) => (parseInt(h.slice(i, i + 2), 16) / 255 || 0).toFixed(3));
  return v.join(' ');
};

const n2 = (n: number) => (Math.round(n * 100) / 100).toString();

// ---- charts -> JPEG ---------------------------------------------------------

interface Img { w: number; h: number; data: Uint8Array }

async function rasterise(el: Element, background?: string): Promise<Img | null> {
  const prepared = await prepareSvg(el, background);
  return prepared && svgToJpeg(prepared.markup, prepared.width, prepared.height);
}

async function prepareSvg(el: Element, background?: string): Promise<{ markup: string; width: number; height: number } | null> {
  const svg = (el.matches('svg') ? el : el.querySelector('.recharts-wrapper > svg') || el.querySelector('svg')) as SVGSVGElement | null;
  if (!svg) return null;
  // Wait for any draw-in animation to finish: the SVG is the same twice in a row.
  for (let i = 0, prev = ''; i < 20; i++) {
    const now = svg.innerHTML;
    if (now === prev) break;
    prev = now;
    await sleep(150);
  }
  const box = svg.getBoundingClientRect();
  if (!box.width || !box.height) return null;

  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(box.width));
  clone.setAttribute('height', String(box.height));
  clone.setAttribute('style', 'font-family: Helvetica, Arial, sans-serif');
  // Print colours: dark text and light grid lines on white, whatever the theme.
  clone.querySelectorAll('.recharts-tooltip-cursor, .recharts-active-dot').forEach((n) => n.remove());
  // Lines draw in by animating a dash over the full path; show the whole line.
  clone.querySelectorAll('.recharts-line-curve').forEach((n) => n.removeAttribute('stroke-dasharray'));
  clone.querySelectorAll('text, tspan').forEach((n) => { n.setAttribute('fill', '#333333'); (n as SVGElement).style.fill = ''; });
  clone.querySelectorAll('.recharts-cartesian-grid line').forEach((n) => n.setAttribute('stroke', '#dddddd'));
  clone.querySelectorAll('.recharts-cartesian-axis-line, .recharts-cartesian-axis-tick-line').forEach((n) => n.setAttribute('stroke', '#999999'));
  if (background) {
    const bg = background.toLowerCase();
    clone.querySelectorAll('[fill], [stroke]').forEach((n) => {
      for (const a of ['fill', 'stroke']) if (n.getAttribute(a)?.toLowerCase() === bg) n.setAttribute(a, '#ffffff');
    });
  }

  return { markup: new XMLSerializer().serializeToString(clone), width: box.width, height: box.height };
}

async function svgToJpeg(xml: string, width: number, height: number): Promise<Img | null> {
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = () => reject(new Error('Could not draw a chart'));
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
  });
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.92));
  if (!blob) return null;
  return { w: canvas.width, h: canvas.height, data: new Uint8Array(await blob.arrayBuffer()) };
}

// ---- layout -----------------------------------------------------------------

class Layout {
  pages: string[][] = [];
  images: Img[] = [];
  y = CONTENT_TOP;
  private ops: string[] = [];

  constructor() { this.newPage(); }

  newPage() { this.ops = []; this.pages.push(this.ops); this.y = CONTENT_TOP; }
  /** Starts a new page unless h points still fit on this one. */
  ensure(h: number) { if (this.y - h < CONTENT_BOTTOM && this.y < CONTENT_TOP) this.newPage(); }

  text(s: string, x: number, y: number, size: number, opts: { bold?: boolean; color?: string } = {}) {
    this.ops.push(`BT ${rgb(opts.color || '#1f2937')} rg /${opts.bold ? 'F2' : 'F1'} ${size} Tf ${n2(x)} ${n2(y)} Td ${pdfStr(s)} Tj ET`);
  }
  rect(x: number, y: number, w: number, h: number, fill?: string, stroke?: string) {
    if (fill) this.ops.push(`${rgb(fill)} rg ${n2(x)} ${n2(y)} ${n2(w)} ${n2(h)} re f`);
    if (stroke) this.ops.push(`${rgb(stroke)} RG 0.6 w ${n2(x)} ${n2(y)} ${n2(w)} ${n2(h)} re S`);
  }
  line(x1: number, y1: number, x2: number, y2: number, color: string, w = 0.6) {
    this.ops.push(`${rgb(color)} RG ${w} w ${n2(x1)} ${n2(y1)} m ${n2(x2)} ${n2(y2)} l S`);
  }
  image(img: Img, x: number, y: number, w: number, h: number) {
    const i = this.images.push(img) - 1;
    this.ops.push(`q ${n2(w)} 0 0 ${n2(h)} ${n2(x)} ${n2(y)} cm /Im${i} Do Q`);
  }
}

function drawKpis(L: Layout, items: PdfKpi[]) {
  const perRow = Math.min(5, items.length);
  const gap = 8;
  const w = (AVAIL - gap * (perRow - 1)) / perRow;
  const h = 56;
  for (let i = 0; i < items.length; i += perRow) {
    L.ensure(h + 10);
    const top = L.y;
    items.slice(i, i + perRow).forEach((k, j) => {
      const x = M + j * (w + gap);
      L.rect(x, top - h, w, h, '#ffffff', '#d5dae1');
      L.rect(x, top - 3, w, 3, k.color);
      L.text(fit(k.label.toUpperCase(), w - 16, 7, true), x + 8, top - 15, 7, { bold: true, color: '#4b5563' });
      L.text(fit(k.value, w - 16, 17, true), x + 8, top - 36, 17, { bold: true, color: '#111827' });
      if (k.sub) L.text(fit(k.sub, w - 16, 7), x + 8, top - 49, 7, { color: '#6b7280' });
    });
    L.y = top - h - 12;
  }
}

async function drawCharts(L: Layout, height: number, items: PdfChart[]) {
  // Shrink a little to fit the rest of the page rather than leave it empty.
  const room = L.y - CONTENT_BOTTOM - 10;
  if (room < height && room >= height * 0.75) height = room;
  L.ensure(height + 10);
  const gap = 10;
  const total = items.reduce((a, c) => a + (c.weight ?? 1), 0);
  const free = AVAIL - gap * (items.length - 1);
  const top = L.y;
  let x = M;
  for (const c of items) {
    const w = (free * (c.weight ?? 1)) / total;
    L.rect(x, top - height, w, height, undefined, '#d5dae1');
    L.text(fit(c.title, w - 16, 9, true), x + 8, top - 14, 9, { bold: true });
    // Legend, wrapping onto more lines as needed.
    let ly = top - 27;
    let lx = x + 8;
    for (const item of c.legend ?? []) {
      const iw = 12 + textW(item.label, 7) + 10;
      if (lx + iw > x + w - 8) { lx = x + 8; ly -= 11; }
      L.rect(lx, ly - 1, 8, 8, item.color);
      L.text(item.label, lx + 11, ly, 7, { color: '#374151' });
      lx += iw;
    }
    const imgTop = (c.legend?.length ? ly : top - 16) - 8;
    const boxH = imgTop - (top - height) - 6;
    const boxW = w - 12;
    const img = c.svg ? await svgToJpeg(c.svg.markup, c.svg.width, c.svg.height)
      : c.el ? await rasterise(c.el, c.background) : null;
    if (img && boxH > 20) {
      const s = Math.min(boxW / img.w, boxH / img.h);
      const iw = img.w * s;
      const ih = img.h * s;
      L.image(img, x + 6 + (boxW - iw) / 2, imgTop - ih - (boxH - ih) / 2, iw, ih);
    } else {
      L.text('Chart not available', x + 8, top - height / 2, 8, { color: '#9ca3af' });
    }
    x += w + gap;
  }
  L.y = top - height - 14;
}

function drawTable(L: Layout, t: { title: string; columns: PdfColumn[]; rows: string[][]; total?: string[] }) {
  const all = [...t.rows, ...(t.total ? [t.total] : [])];
  const natural = (size: number) => t.columns.map((c, i) =>
    Math.max(textW(c.label, size, true), ...all.map((r) => textW(r[i] ?? '', size, true))) + 10);
  let size = 8;
  let widths = natural(size);
  if (widths.reduce((a, b) => a + b, 0) > AVAIL) { size = 7; widths = natural(size); }
  const sum = widths.reduce((a, b) => a + b, 0);
  widths = widths.map((w) => (w * AVAIL) / sum); // fill the page width, or shrink to it
  const rowH = size + 7;

  const header = (cont: boolean) => {
    L.text(t.title + (cont ? ' (continued)' : ''), M, L.y - 10, 10, { bold: true });
    L.y -= 18;
    L.rect(M, L.y - rowH, AVAIL, rowH, '#e8ecf1');
    let x = M;
    t.columns.forEach((c, i) => {
      const s = fit(c.label, widths[i] - 8, size, true);
      const tx = c.align === 'right' ? x + widths[i] - 4 - textW(s, size, true) : x + 4;
      L.text(s, tx, L.y - rowH + 4.5, size, { bold: true, color: '#111827' });
      x += widths[i];
    });
    L.y -= rowH;
  };
  const row = (r: string[], k: number, bold: boolean) => {
    if (L.y - rowH < CONTENT_BOTTOM) { L.newPage(); header(true); }
    if (bold) {
      L.rect(M, L.y - rowH, AVAIL, rowH, '#e8ecf1');
      L.line(M, L.y, M + AVAIL, L.y, '#9ca3af', 0.8);
    } else if (k % 2) L.rect(M, L.y - rowH, AVAIL, rowH, '#f6f7f9');
    let x = M;
    t.columns.forEach((c, i) => {
      const s = fit(r[i] ?? '', widths[i] - 8, size, bold);
      const tx = c.align === 'right' ? x + widths[i] - 4 - textW(s, size, bold) : x + 4;
      L.text(s, tx, L.y - rowH + 4.5, size, { bold, color: '#1f2937' });
      x += widths[i];
    });
    L.y -= rowH;
  };

  L.ensure(18 + rowH * Math.min(4, all.length + 1));
  header(false);
  t.rows.forEach((r, k) => row(r, k, false));
  if (t.total) row(t.total, 0, true);
  if (!all.length) { L.text('No data for the selected filters.', M + 4, L.y - 12, 8, { color: '#6b7280' }); L.y -= 16; }
  L.line(M, L.y, M + AVAIL, L.y, '#d5dae1');
  L.y -= 16;
}

function drawNote(L: Layout, text: string) {
  const lines = wrap(text, AVAIL, 7.5);
  for (const line of lines) {
    L.ensure(11);
    L.text(line, M, L.y - 8, 7.5, { color: '#6b7280' });
    L.y -= 11;
  }
  L.y -= 6;
}

// ---- file -------------------------------------------------------------------

const latin1 = (s: string) => Uint8Array.from(s, (c) => c.charCodeAt(0) & 255);

function build(spec: PdfSpec, L: Layout): Blob {
  const generated = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const nPages = L.pages.length;
  // Header and footer on every page, now that the page count is known.
  const head = (i: number) => {
    const o: string[] = [];
    const t = (s: string, x: number, y: number, size: number, bold: boolean, color: string) =>
      o.push(`BT ${rgb(color)} rg /${bold ? 'F2' : 'F1'} ${size} Tf ${n2(x)} ${n2(y)} Td ${pdfStr(s)} Tj ET`);
    t(fit(spec.title, AVAIL - 180, 14, true), M, PH - M - 12, 14, true, '#111827');
    if (spec.status) t(fit(spec.status, AVAIL - 180, 9.5, true), M, PH - M - 26, 9.5, true, '#C62828');
    t(fit(spec.subtitle, AVAIL - 180, 8, false), M, PH - M - (spec.status ? 38 : 25), 8, false, '#4b5563');
    const g = `Generated ${generated}`;
    t(g, PW - M - textW(g, 8), PH - M - 12, 8, false, '#4b5563');
    const rule = PH - M - (spec.status ? 44 : 32);
    o.push(`${rgb('#1f2937')} RG 1 w ${M} ${rule} m ${PW - M} ${rule} l S`);
    const p = `Page ${i + 1} of ${nPages}`;
    t(p, PW - M - textW(p, 7), M - 4, 7, false, '#6b7280');
    t('SDICS', M, M - 4, 7, true, '#6b7280');
    return o.join('\n');
  };

  const parts: Uint8Array[] = [];
  let len = 0;
  const offsets: number[] = [];
  const push = (b: string | Uint8Array) => { const u = typeof b === 'string' ? latin1(b) : b; parts.push(u); len += u.length; };
  const obj = (n: number, ...body: (string | Uint8Array)[]) => { offsets[n] = len; push(`${n} 0 obj\n`); body.forEach(push); push('\nendobj\n'); };

  push('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n');
  // 1 catalog, 2 page tree, 3-4 fonts, then images, then a content + page pair per page.
  const imgBase = 5;
  const pageBase = imgBase + L.images.length;
  const pageIds = L.pages.map((_, i) => pageBase + i * 2 + 1);
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, `<< /Type /Pages /Count ${nPages} /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] >>`);
  obj(3, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  obj(4, '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  L.images.forEach((img, i) => obj(imgBase + i,
    `<< /Type /XObject /Subtype /Image /Width ${img.w} /Height ${img.h} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${img.data.length} >>\nstream\n`,
    img.data, '\nendstream'));
  const xobjects = L.images.map((_, i) => `/Im${i} ${imgBase + i} 0 R`).join(' ');
  L.pages.forEach((ops, i) => {
    const content = latin1(head(i) + '\n' + ops.join('\n'));
    obj(pageBase + i * 2, `<< /Length ${content.length} >>\nstream\n`, content, '\nendstream');
    obj(pageIds[i], `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PW} ${PH}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >>${xobjects ? ` /XObject << ${xobjects} >>` : ''} >> /Contents ${pageBase + i * 2} 0 R >>`);
  });

  const count = pageBase + nPages * 2;
  const xref = len;
  push(`xref\n0 ${count}\n0000000000 65535 f \n`);
  for (let n = 1; n < count; n++) push(`${String(offsets[n]).padStart(10, '0')} 00000 n \n`);
  push(`trailer\n<< /Size ${count} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts as BlobPart[], { type: 'application/pdf' });
}

/** Lays out the blocks on landscape A4 pages and downloads the PDF. */
export async function exportPdf(spec: PdfSpec): Promise<void> {
  const L = new Layout();
  for (const b of spec.blocks) {
    if (b.kind === 'kpis') drawKpis(L, b.items);
    else if (b.kind === 'charts') await drawCharts(L, b.height, b.items);
    else if (b.kind === 'table') drawTable(L, b);
    else if (b.kind === 'note') drawNote(L, b.text);
    else if (b.kind === 'pageBreak' && L.y < CONTENT_TOP) L.newPage();
  }
  const blob = build(spec, L);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = spec.fileName.endsWith('.pdf') ? spec.fileName : `${spec.fileName}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Resolves once test() is truthy (checked every 100 ms), or rejects after ms. */
export function waitFor(test: () => unknown, ms = 15000): Promise<void> {
  const start = Date.now();
  return new Promise((resolve, reject) => {
    const tick = () => {
      if (test()) resolve();
      else if (Date.now() - start > ms) reject(new Error('Timed out waiting for the charts to load'));
      else setTimeout(tick, 100);
    };
    tick();
  });
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
